import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import {
  dtmfOptionsInitialValue,
  forwardingTypeOptions,
  maxRetriesOptions,
  queueDropTimeOptions
} from '../../../utils/common';
import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import {
  audioPromptsAllOptionGet,
  didProcessOptionGet,
  didUserOptionGet,
  ivrAdd,
  queuesOptionGet,
  ivrUpdate,
  ivrOptionsGet,
  ivrDetailsGetById
} from '../../../store/slices/tenantSlices/incomingSlice';
import { callexAiAgentOptionsGet } from '../../../store/slices/tenantSlices/configurationSlice';

const AddEditIvrModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [userOptions, setUserOptions] = useState([]);
  const [queuesOption, setQueuesOption] = useState([]);
  const [ivrOption, setIvrOption] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [audioPromptOption, setAudioPromptOption] = useState([]);
  const [dtmfOptions, setDtmfOptions] = useState([...dtmfOptionsInitialValue]);
  const [aiPhoneNumberOption, setAiPhoneNumberOption] = useState([]);

  const initialValues = {
    name: '',
    description: '',
    ivr_prompt: '',
    wait_duration: '',
    invalid_prompt: '',
    exit_prompt: '',
    max_retries: '',
    status: 'true',
    dtmf_options: []
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('name is required'),
    description: Yup.string().required('description is required'),
    ivr_prompt: Yup.string().required('Ivr prompt is required'),
    wait_duration: Yup.string().required('Wait duration is required'),
    invalid_prompt: Yup.string().required('invalid prompt is required'),
    exit_prompt: Yup.string().required('exit prompt is required'),
    max_retries: Yup.string().required('max retries is required')
  });

  const onSubmit = async () => {
    const filteredDtmfOptions = dtmfOptions.filter(
      (option) => option.forwarding_type && option.forwarding_option
    );

    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id, dtmf_options: filteredDtmfOptions };
        const response = await dispatch(ivrUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          setDtmfOptions([...dtmfOptionsInitialValue]);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values, dtmf_options: filteredDtmfOptions };
        const response = await dispatch(ivrAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          setDtmfOptions([...dtmfOptionsInitialValue]);
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchIvrDetailsGetById = async () => {
    try {
      const response = await dispatch(ivrDetailsGetById(editRecord?.id)).unwrap();
      console.log('response', response);
      setValues({
        ...response?.ivr,
        status: String(response?.ivr?.status),
        ivr_prompt: String(response?.ivr?.ivr_prompt || ''),
        wait_duration: Number(response?.ivr?.wait_duration || ''),
        invalid_prompt: String(response?.ivr?.invalid_prompt || ''),
        exit_prompt: String(response?.ivr?.exit_prompt || ''),
        max_retries: String(response?.ivr?.max_retries || '')
      });

      const updatedDtmfOptions = [...dtmfOptionsInitialValue];

      response?.dtmf_options?.forEach((option) => {
        const index = updatedDtmfOptions.findIndex((dtmfOption) => dtmfOption.dtmf === option.dtmf);
        if (index !== -1) {
          updatedDtmfOptions[index] = {
            ...updatedDtmfOptions[index],
            forwarding_type: option.forwarding_type || '',
            forwarding_option: option.forwarding_option || ''
          };
        }
      });

      setDtmfOptions(updatedDtmfOptions);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchIvrDetailsGetById();
    }
  }, [editRecord]);

  const getIvrOption = async () => {
    try {
      const response = await dispatch(ivrOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setIvrOption(options);
      } else {
        setIvrOption([]);
      }
    } catch (error) {
      setIvrOption([]);
      console.log('error', error);
    }
  };

  const getAudioPromptOption = async () => {
    try {
      const response = await dispatch(audioPromptsAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setAudioPromptOption(options);
      } else {
        setAudioPromptOption([]);
      }
    } catch (error) {
      setAudioPromptOption([]);
      console.log('error', error);
    }
  };

  const getQueuesOption = async () => {
    try {
      const response = await dispatch(queuesOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setQueuesOption(options);
      } else {
        setQueuesOption([]);
      }
    } catch (error) {
      setQueuesOption([]);
      console.log('error', error);
    }
  };

  const getProcessOption = async () => {
    try {
      const response = await dispatch(didProcessOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const response = await dispatch(didUserOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: String(val.id)
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  const getAIAgentOption = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id) || ""
        }));
        setAiPhoneNumberOption(options);
      } else {
        setAiPhoneNumberOption([]);
      }
    } catch (error) {
      setAiPhoneNumberOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getIvrOption();
    getAudioPromptOption();
    getQueuesOption();
    getProcessOption();
    getUserOption();
    getAIAgentOption();
  }, []);

  const getForwardingOptions = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_process') {
      return processOptions;
    }
    if (forwarding_type === 'transfer_to_queue') {
      return queuesOption;
    }
    if (forwarding_type === 'transfer_to_playback') {
      return audioPromptOption;
    }
    if (forwarding_type === 'transfer_to_agent') {
      return userOptions;
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return ivrOption;
    }
    if (forwarding_type === 'transfer_to_ai_number') {
      return aiPhoneNumberOption;
    }
    return [];
  };

  const getForwardingLabel = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_process') {
      return 'Process';
    }
    if (forwarding_type === 'transfer_to_queue') {
      return 'Queue';
    }
    if (forwarding_type === 'transfer_to_playback') {
      return 'Playback';
    }
    if (forwarding_type === 'transfer_to_agent') {
      return 'User';
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return 'IVR';
    }
    return '';
  };

  const isAgentForwarding = (forwarding_type) => forwarding_type === 'transfer_to_agent';

  const getForwardingSelectValue = (forwarding_type, value) =>
    isAgentForwarding(forwarding_type)
      ? value
        ? String(value).split(',').filter(Boolean)
        : []
      : value;

  const wrapForwardingOnChange = (forwarding_type, name, onChange) =>
    isAgentForwarding(forwarding_type)
      ? (e) =>
          onChange({
            target: {
              name,
              value: Array.isArray(e.target.value) ? e.target.value.join(',') : e.target.value
            }
          })
      : onChange;

  const handleDtmfChange = (index, event) => {
    const { name, value } = event.target;
    const updatedDtmf = [...dtmfOptions];
    updatedDtmf[index] = { ...updatedDtmf[index], [name]: value };
    if (name === 'forwarding_type') {
      updatedDtmf[index] = {
        ...updatedDtmf[index],
        forwarding_option: value === 'transfer_to_hangup' ? 'Hangup' : ''
      };
    }
    setDtmfOptions(updatedDtmf);
  };

  const forwardingSelectOptions = [
    'transfer_to_process',
    'transfer_to_queue',
    'transfer_to_playback',
    'transfer_to_agent',
    'transfer_to_ivr',
    'transfer_to_ai_number'
  ];

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit IVRs' : 'Add IVRs'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="ivr" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="description"
                label="Description"
                placeholder="Enter description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="ivr_prompt"
                label="IVR Prompt"
                placeholder="Select IVR Prompt"
                value={values.ivr_prompt}
                options={audioPromptOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="wait_duration"
                label="Wait Duration"
                placeholder="Select Wait Duration"
                value={values.wait_duration}
                options={queueDropTimeOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="invalid_prompt"
                label="Invalid Prompt"
                placeholder="Select Invalid Prompt"
                value={values.invalid_prompt}
                options={audioPromptOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="exit_prompt"
                label="Exit Prompt"
                placeholder="Select Exit Prompt"
                value={values.exit_prompt}
                options={audioPromptOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="max_retries"
                label="Max Retries"
                placeholder="Select Max Retries"
                value={values.max_retries}
                options={maxRetriesOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
          </Row>
          <div className="w-100 py-2 px-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
            <div>DTMF Options</div>
          </div>
          <Row className="pt-4 pb-2">
            <Col lg="1" md="2" className="text-center fw-bold text-primary fs-6">
              DTMF
            </Col>
            <Col lg="4" md="4" className="text-center fw-bold text-primary fs-6">
              Action
            </Col>
            <Col lg="4" md="4" className="text-center fw-bold text-primary fs-6">
              Parameter
            </Col>
          </Row>
          {dtmfOptions?.map((val, index) => {
            return (
              <Row key={index} className="py-2">
                <Col lg="1" md="2" className="text-center text-primary fs-6">
                  {val?.dtmf}
                </Col>
                <Col lg="4" md="4">
                  <FormSelect
                    className="callex-mb-0"
                    isShowLabel={false}
                    name="forwarding_type"
                    placeholder="Select Forwarding Type"
                    value={val.forwarding_type}
                    options={forwardingTypeOptions}
                    onChange={(e) => handleDtmfChange(index, e)}
                  />
                </Col>

                {val.forwarding_type !== '' ? (
                  <Col lg="4" md="4">
                    {forwardingSelectOptions.includes(val.forwarding_type) ? (
                      <FormSelect
                        className="callex-mb-0"
                        isShowLabel={false}
                        name="forwarding_option"
                        placeholder={`Select ${getForwardingLabel(val.forwarding_type)}`}
                        value={getForwardingSelectValue(val.forwarding_type, val.forwarding_option)}
                        options={getForwardingOptions(val.forwarding_type)}
                        isMulti={isAgentForwarding(val.forwarding_type)}
                        onChange={wrapForwardingOnChange(
                          val.forwarding_type,
                          'forwarding_option',
                          (e) => handleDtmfChange(index, e)
                        )}
                      />
                    ) : val.forwarding_type === 'transfer_to_call_forward' ? (
                      <FormInput
                        className="callex-mb-0"
                        isShowLabel={false}
                        name="forwarding_option"
                        label="Call Forward (PSTN)"
                        placeholder="Enter Call Forward"
                        value={val.forwarding_option}
                        onChange={(e) => handleDtmfChange(index, e)}
                      />
                    ) : val.forwarding_type === 'transfer_to_hangup' ? (
                      <FormInput
                        className="callex-mb-0"
                        isShowLabel={false}
                        name="forwarding_option"
                        label="Hangup"
                        placeholder="Enter Hangup"
                        value={val.forwarding_option}
                        onChange={(e) => handleDtmfChange(index, e)}
                        disabled={true}
                      />
                    ) : null}
                  </Col>
                ) : null}
              </Row>
            );
          })}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setDtmfOptions([...dtmfOptionsInitialValue]);
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="ivr" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditIvrModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditIvrModal;
